// IST (Asia/Kolkata) trading-day toolkit — the single authoritative source for
// IST date/block math across the console.
//
// The platform's trading days are IST 96-block days: block 1 = 00:00 IST, and
// block N covers [(N-1)*15, N*15) minutes of the IST day. Browser-local or UTC
// date math silently shifts the trading day for users outside IST (and for IST
// users between 00:00–05:30), so every page must derive dates/blocks from the
// Intl "Asia/Kolkata" formatters below instead of hand-rolling offsets.

const IST_DAY = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Kolkata",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const IST_CLOCK = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Asia/Kolkata",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

/** IST calendar date "YYYY-MM-DD", offset by N days from now (negative = past). */
export function istDate(offsetDays: number): string {
  return IST_DAY.format(new Date(Date.now() + offsetDays * 86_400_000));
}

/** Today's IST trading date, "YYYY-MM-DD". */
export function istToday(): string {
  return istDate(0);
}

/** Tomorrow's IST trading date, "YYYY-MM-DD" (the day-ahead delivery date). */
export function istTomorrow(): string {
  return istDate(1);
}

/** Current 15-min IST block of the day, clamped to 1..96. */
export function istNowBlock(): number {
  const [h = 0, m = 0] = IST_CLOCK.format(new Date()).split(":").map(Number);
  return Math.min(96, Math.max(1, Math.floor((h * 60 + m) / 15) + 1));
}

/** 1-based 15-min block index → "HH:MM" IST clock label (block 1 → "00:00"). */
export function blockTime(block: number): string {
  const mins = (block - 1) * 15;
  return `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
}

/**
 * IST calendar day "YYYY-MM-DD" of an ISO timestamp. Unlike `iso.slice(0, 10)`,
 * this is correct for timestamps that arrive in UTC ("…Z") or any other offset.
 */
export function istDayOf(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso.slice(0, 10) : IST_DAY.format(d);
}

// ---------------------------------------------------------------------------
// DISPLAY formatters — DD-MM-YYYY app-wide. The canonical data key / API value
// stays "YYYY-MM-DD" (istDate); these are for what the user SEES only.
// ---------------------------------------------------------------------------

/** Coerce a "YYYY-MM-DD" or ISO timestamp to the IST "YYYY-MM-DD" calendar day. */
function _ymd(s: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : istDayOf(s);
}

/** Display a date as "DD-MM-YYYY" (accepts "YYYY-MM-DD" or an ISO timestamp). */
export function fmtDMY(s: string): string {
  const [y, m, d] = _ymd(s).split("-");
  return y && m && d ? `${d}-${m}-${y}` : s;
}

/** Compact "DD-MM" (for dense axis / heatmap row labels). */
export function fmtDM(s: string): string {
  const [, m, d] = _ymd(s).split("-");
  return m && d ? `${d}-${m}` : s;
}

/** "DD-MM-YYYY HH:MM" (IST) for an ISO timestamp. */
export function fmtDMYTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${fmtDMY(IST_DAY.format(d))} ${IST_CLOCK.format(d)}`;
}

/** Compact "DD-MM HH:MM" (IST) for dense datetime axis ticks. */
export function fmtDMHM(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${fmtDM(IST_DAY.format(d))} ${IST_CLOCK.format(d)}`;
}

/**
 * Trim a chart series to its data span: drop leading and trailing rows that
 * carry no value, so the x-axis STARTS at the first day with data and ENDS at
 * the last (instead of padding a fixed window with empty dates). Interior gaps
 * are preserved (honest breaks in the line).
 */
export function trimSeriesToData<T>(rows: T[], hasData: (row: T) => boolean): T[] {
  let start = 0;
  let end = rows.length - 1;
  while (start <= end && !hasData(rows[start]!)) start++;
  while (end >= start && !hasData(rows[end]!)) end--;
  return start > end ? [] : rows.slice(start, end + 1);
}

// ---------------------------------------------------------------------------
// FRESHNESS — the single source of "how old is this value, and is it stale?"
// so every widget across the console shows age + a consistent stale state.
// ---------------------------------------------------------------------------

/**
 * Human "… ago" for an ISO timestamp (the canonical relative-time formatter the
 * data widgets share). Returns "—" for null / unparseable input.
 */
export function relTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return "—";
  const mins = Math.round(ms / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs} h ago`;
  return `${Math.round(hrs / 24)} d ago`;
}

export type FreshnessTone = "green" | "amber" | "red";

export interface FreshnessPill {
  tone: FreshnessTone;
  /** Short relative-age label, e.g. "3 min ago" / "26 h ago" / "no data". */
  label: string;
  /** True once the value is past its stale threshold (or has no timestamp). */
  isStale: boolean;
}

/**
 * Map a value's last-update timestamp to a freshness verdict for the UI:
 *   • green  — fresher than `liveMin`
 *   • amber  — between `liveMin` and `staleMin` (aging, not yet stale)
 *   • red    — older than `staleMin`, or no timestamp at all (isStale)
 *
 * `liveMin`/`staleMin` are minutes and should be cadence-matched to the feed
 * behind the value (default 30 / 120). Use the returned tone to colour a dot or
 * Pill and `isStale` to dim/flag the widget; `label` is a ready relative age.
 */
export function dataFreshnessPill(
  lastUpdateIso: string | null | undefined,
  opts?: { liveMin?: number; staleMin?: number },
): FreshnessPill {
  const liveMin = opts?.liveMin ?? 30;
  const staleMin = opts?.staleMin ?? 120;
  if (!lastUpdateIso) return { tone: "red", label: "no data", isStale: true };
  const ms = Date.now() - new Date(lastUpdateIso).getTime();
  if (!Number.isFinite(ms)) return { tone: "red", label: "no data", isStale: true };
  const mins = ms / 60_000;
  const label = relTime(lastUpdateIso);
  if (mins <= liveMin) return { tone: "green", label, isStale: false };
  if (mins <= staleMin) return { tone: "amber", label, isStale: false };
  return { tone: "red", label, isStale: true };
}
